import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./Workshops.css";
import LoadingOverlay from "../../components/Loading/LoadingOverlay";
import CustomAlert from "../../components/Alerts/CustomAlert";
import Modal from "../../components/Dialog/Modal";
import { addAlert, removeAlert } from "../../Utils/helper";
import {
  WORKSHOP_TYPE_OPTIONS,
  deleteWorkshop,
  duplicateWorkshop,
  getWorkshop,
  toggleWorkshopActive,
} from "../../Utils/workshopApi";

const WorkshopDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workshop, setWorkshop] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleAlert = (title, message, type) =>
    addAlert(title, message, type, true, setAlerts);

  const load = async () => {
    setIsLoading(true);
    try {
      const ws = await getWorkshop(id);
      setWorkshop(ws);
    } catch (err) {
      console.error("Failed to load workshop", err);
      handleAlert("Error", "Could not load workshop.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onToggle = async () => {
    try {
      await toggleWorkshopActive(id);
      handleAlert("Updated", "Status changed.", "success");
      load();
    } catch {
      handleAlert("Error", "Could not toggle status.", "error");
    }
  };

  const onDuplicate = async () => {
    try {
      const created = await duplicateWorkshop(id);
      handleAlert("Duplicated", "Created a draft copy.", "success");
      if (created?.id) navigate(`/workshops/${created.id}/edit`);
    } catch {
      handleAlert("Error", "Could not duplicate.", "error");
    }
  };

  const onDelete = async () => {
    try {
      await deleteWorkshop(id);
      handleAlert("Deleted", "Workshop removed.", "success");
      setTimeout(() => navigate("/workshops"), 600);
    } catch {
      handleAlert("Error", "Could not delete.", "error");
      setConfirmDelete(false);
    }
  };

  if (!workshop && !isLoading) {
    return (
      <div className="main-container">
        <div className="ws-details-container">
          <p>Workshop not found.</p>
          <button className="ws-btn" onClick={() => navigate("/workshops")}>
            ← Back to workshops
          </button>
        </div>
      </div>
    );
  }
  if (!workshop) return <LoadingOverlay />;

  const typeLabel =
    WORKSHOP_TYPE_OPTIONS.find((o) => o.value === workshop.workshop_type)
      ?.label || workshop.workshop_type;
  const formatLabel = workshop.type?.title || "—";
  const formatPrice = () => {
    const inr = workshop.selling_price ?? workshop.mrp;
    const usd = workshop.selling_price_usd ?? workshop.mrp_usd;
    const parts = [];
    if (inr) parts.push(`₹${Number(inr).toLocaleString("en-IN")}`);
    if (usd) parts.push(`$${Number(usd).toLocaleString("en-US")}`);
    return parts.join(" / ") || "Free";
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className="main-container">
        <div className="ws-details-container">
          <div className="ws-list-header">
            <div className="ws-list-header-left">
              <button
                className="ws-link-btn"
                onClick={() => navigate("/workshops")}
              >
                ← All workshops
              </button>
            </div>
            <div className="ws-list-header-right" style={{ gap: 8, display: "flex" }}>
              <button
                className="ws-btn"
                onClick={() => navigate(`/workshops/${id}/edit`)}
              >
                Edit
              </button>
              <button className="ws-btn" onClick={onToggle}>
                {workshop.is_active ? "Unpublish" : "Publish"}
              </button>
              <button className="ws-btn" onClick={onDuplicate}>
                Duplicate
              </button>
              <button
                className="ws-btn danger"
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </button>
            </div>
          </div>

          <div className="ws-details-banner">
            {workshop.file ? (
              <img src={workshop.file} alt={workshop.title} />
            ) : (
              <div
                className="ws-card-banner-placeholder"
                style={{ width: "100%", height: "100%" }}
              >
                {workshop.title?.[0]?.toUpperCase() || "W"}
              </div>
            )}
          </div>

          <h1 style={{ margin: "0 0 8px 0" }}>{workshop.title}</h1>
          <div style={{ color: "#666", marginBottom: 24 }}>
            {workshop.description}
          </div>

          <div className="ws-details-grid">
            <div>
              <h3>About</h3>
              <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                {workshop.about_workshop || "—"}
              </p>

              {workshop.sections?.length > 0 && (
                <>
                  <h3 style={{ marginTop: 24 }}>Curriculum</h3>
                  {workshop.sections.map((s, si) => (
                    <div key={si} className="ws-section">
                      <div className="ws-section-header">
                        <strong>
                          {si + 1}. {s.title}
                        </strong>
                      </div>
                      <div className="ws-section-body">
                        {s.description && (
                          <p style={{ color: "#666", marginTop: 0 }}>
                            {s.description}
                          </p>
                        )}
                        {(s.chapters || []).map((c, ci) => (
                          <div className="ws-chapter" key={ci}>
                            <strong>
                              {si + 1}.{ci + 1} {c.title}
                            </strong>
                            {c.description && (
                              <p style={{ color: "#777", margin: "4px 0" }}>
                                {c.description}
                              </p>
                            )}
                            <ul style={{ margin: "6px 0", paddingLeft: 18 }}>
                              {(c.videos || []).map((v, vi) => (
                                <li key={vi} style={{ fontSize: 13 }}>
                                  {v.title}
                                  {v.video_url && (
                                    <>
                                      {" — "}
                                      <a
                                        href={v.video_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        link
                                      </a>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {workshop.faqs?.length > 0 && (
                <>
                  <h3 style={{ marginTop: 24 }}>FAQs</h3>
                  {workshop.faqs.map((f, i) => (
                    <div className="ws-review-card" key={i}>
                      <h4>{f.title}</h4>
                      {(f.answers || []).map((a, j) => (
                        <p key={j} style={{ margin: 0, color: "#444" }}>
                          {a.answer}
                        </p>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </div>

            <aside className="ws-details-side">
              <div className="ws-review-card">
                <h4>Status</h4>
                <span
                  className={`ws-status ${
                    workshop.is_active ? "published" : "draft"
                  }`}
                  style={{ position: "static", display: "inline-block" }}
                >
                  {workshop.is_active ? "Published" : "Draft"}
                </span>
              </div>
              <div className="ws-review-card">
                <h4>Overview</h4>
                <dl>
                  <dt>Category</dt>
                  <dd>{typeLabel}</dd>
                  <dt>Format</dt>
                  <dd>{formatLabel}</dd>
                  <dt>Price</dt>
                  <dd>{formatPrice()}</dd>
                  <dt>Seats</dt>
                  <dd>
                    {workshop.is_unlimited_seats
                      ? "Unlimited"
                      : workshop.seats || "—"}
                  </dd>
                  <dt>Enrolled</dt>
                  <dd>{workshop.enrolments || 0}</dd>
                  <dt>Views</dt>
                  <dd>{workshop.view_count || 0}</dd>
                </dl>
              </div>
              {workshop.workshop_experts?.length > 0 && (
                <div className="ws-review-card">
                  <h4>Experts</h4>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {workshop.workshop_experts.map((e) => (
                      <li key={e.id}>{e.title}</li>
                    ))}
                  </ul>
                </div>
              )}
              {workshop.tags?.length > 0 && (
                <div className="ws-review-card">
                  <h4>Tags</h4>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {workshop.tags.map((t) => (
                      <span className="ws-chip" key={t.id}>
                        {t.title}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>

        {confirmDelete && (
          <Modal title="Delete workshop?" onClose={() => setConfirmDelete(false)}>
            <p>
              This permanently deletes <strong>{workshop.title}</strong> with all
              its sections, chapters and videos.
            </p>
            <div className="confirmation-buttons" style={{ marginTop: 16 }}>
              <button
                className="ws-btn"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </button>
              <button className="ws-btn danger" onClick={onDelete}>
                Delete
              </button>
            </div>
          </Modal>
        )}

        {alerts.map((alert) => (
          <CustomAlert
            key={alert.id}
            title={alert.title}
            message={alert.message}
            type={alert.type}
            autoClose={alert.autoClose}
            onClose={() => removeAlert(alert.id, setAlerts)}
          />
        ))}
      </div>
    </>
  );
};

export default WorkshopDetails;
